import type { EnterpriseUserUsage, UserStatsResponse } from '@/api/usage'

export type LeaderboardTimeRange = 'thisWeek' | 'allTime'
export type LeaderboardMetric = 'words' | 'streak'

export interface LeaderboardEntry {
  rank: number
  name: string
  email: string
  value: number
  avatar: string
  isYou: boolean
  userId: string
}

function getSafeNumber(value: number | null | undefined): number {
  return value ?? 0
}

type MemberWithStats = EnterpriseUserUsage & { stats: UserStatsResponse }

function hasStats(member: EnterpriseUserUsage): member is MemberWithStats {
  return member.stats != null
}

function getValue(member: MemberWithStats, metric: LeaderboardMetric, timeRange: LeaderboardTimeRange): number {
  if (metric === 'streak') return getSafeNumber(member.stats.day_streak)
  return timeRange === 'thisWeek'
    ? getSafeNumber(member.stats.words_this_week)
    : getSafeNumber(member.stats.total_words)
}

export function buildLeaderboardData(
  members: EnterpriseUserUsage[],
  metric: LeaderboardMetric,
  timeRange: LeaderboardTimeRange,
  currentUserId: string
): LeaderboardEntry[] {
  const validMembers = members.filter(hasStats)

  const sorted = [...validMembers].sort(
    (a, b) => getValue(b, metric, timeRange) - getValue(a, metric, timeRange)
  )

  let currentRank = 1
  return sorted.map((member, index) => {
    const value = getValue(member, metric, timeRange)

    // Assign rank: if this member has the same value as the previous, share the rank
    if (index > 0 && value !== getValue(sorted[index - 1], metric, timeRange)) {
      currentRank = index + 1
    }

    const name =
      [member.first_name, member.last_name].filter(Boolean).join(' ').trim() || member.email

    return {
      rank: currentRank,
      name,
      email: member.email,
      value,
      avatar: member.first_name?.charAt(0).toUpperCase() || '?',
      isYou: member.user_id === currentUserId,
      userId: member.user_id,
    }
  })
}
